import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { detectMimeType, getExtensionFromMime } from '@/utils/imageUtils';

export interface BulkImage {
  id: string;
  file: File;
  preview: string;
  enhancedUrl?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
}

export interface BulkProcessingStats {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  remaining: number;
  currentBatch: number;
  totalBatches: number;
  estimatedTimeRemaining: string;
  startTime?: number;
}

export interface EnhancementSettings {
  enhancementType: 'dpi' | 'quality' | 'both';
  dpiTarget: number;
  qualityLevel: 'standard' | 'hd' | 'ultra';
  watermark?: {
    text: string;
    position: string;
    opacity: number;
    size: string;
    color: string;
    background: boolean;
  };
}

const BATCH_SIZE = 5;
const BATCH_DELAY = 800;
const MAX_RETRIES = 2;
const RETRY_DELAY = 2000;

export const useBulkImageEnhancement = () => {
  const [images, setImages] = useState<BulkImage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [stats, setStats] = useState<BulkProcessingStats>({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    remaining: 0,
    currentBatch: 0,
    totalBatches: 0,
    estimatedTimeRemaining: '--'
  });
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const pauseRef = useRef(false);
  const abortRef = useRef(false);

  const addImages = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files).filter(f => f.type.startsWith('image/'));
    
    if (images.length + fileArray.length > 250) {
      toast.error(`Maximum 250 images allowed. You can add ${250 - images.length} more.`);
      return;
    }

    const newImages: BulkImage[] = fileArray.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substring(7)}`,
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      progress: 0
    }));

    setImages(prev => [...prev, ...newImages]);
    setStats(prev => ({
      ...prev,
      total: prev.total + newImages.length,
      remaining: prev.remaining + newImages.length,
      totalBatches: Math.ceil((prev.total + newImages.length) / BATCH_SIZE)
    }));
    
    toast.success(`Added ${newImages.length} image(s)`);
  }, [images.length]);

  const removeImage = useCallback((id: string) => {
    setImages(prev => {
      const image = prev.find(img => img.id === id);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      const filtered = prev.filter(img => img.id !== id);
      setStats(s => ({
        ...s,
        total: filtered.length,
        remaining: filtered.filter(i => i.status === 'pending').length,
        totalBatches: Math.ceil(filtered.length / BATCH_SIZE)
      }));
      return filtered;
    });
  }, []);

  const clearAll = useCallback(() => {
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    setStats({
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      remaining: 0,
      currentBatch: 0,
      totalBatches: 0,
      estimatedTimeRemaining: '--'
    });
  }, [images]);

  const prepareImageData = async (image: BulkImage, settings: EnhancementSettings): Promise<string> => {
    const readAsDataURL = (file: File) => new Promise<string>((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(file);
    });

    const src = await readAsDataURL(image.file);
    const maxDim = (settings.enhancementType === 'dpi' && settings.dpiTarget >= 600) ? 1600 : 1280;

    const imgEl = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = (e) => reject(e);
      el.src = src;
    });

    const largestSide = Math.max(imgEl.width, imgEl.height);
    const scale = Math.min(1, maxDim / (largestSide || maxDim));

    if (scale >= 1) return src;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(imgEl.width * scale);
    canvas.height = Math.round(imgEl.height * scale);
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);
    }

    const mime = image.file.type.includes('png') ? 'image/png' : 'image/jpeg';
    return canvas.toDataURL(mime, mime === 'image/jpeg' ? 0.9 : undefined as any);
  };

  const enhanceSingleImage = async (
    image: BulkImage, 
    settings: EnhancementSettings,
    retryCount = 0
  ): Promise<boolean> => {
    try {
      setImages(prev => prev.map(img => 
        img.id === image.id ? { ...img, status: 'processing', progress: 20 } : img
      ));

      const base64Data = await prepareImageData(image, settings);

      setImages(prev => prev.map(img => 
        img.id === image.id ? { ...img, progress: 40 } : img
      ));

      const { data, error } = await supabase.functions.invoke('enhance-image-quality', {
        body: {
          imageUrl: base64Data,
          enhancementType: settings.enhancementType,
          dpiTarget: settings.enhancementType === 'dpi' || settings.enhancementType === 'both' ? settings.dpiTarget : undefined,
          qualityLevel: settings.enhancementType === 'quality' || settings.enhancementType === 'both' ? settings.qualityLevel : undefined,
          watermark: settings.watermark
        }
      });

      if (error) {
        // Handle rate limiting with retry
        if (error.message?.includes('429') && retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return enhanceSingleImage(image, settings, retryCount + 1);
        }
        throw error;
      }

      setImages(prev => prev.map(img => 
        img.id === image.id 
          ? { ...img, status: 'completed', progress: 100, enhancedUrl: data.enhancedImageUrl }
          : img
      ));

      return true;
    } catch (error: any) {
      console.error('Enhancement error:', error);
      setImages(prev => prev.map(img => 
        img.id === image.id ? { ...img, status: 'failed', progress: 0, error: error.message } : img
      ));
      return false;
    }
  };

  const processImages = async (settings: EnhancementSettings) => {
    const pendingImages = images.filter(img => img.status === 'pending');
    
    if (pendingImages.length === 0) {
      toast.error('No pending images to process');
      return;
    }

    setIsProcessing(true);
    setIsPaused(false);
    pauseRef.current = false;
    abortRef.current = false;

    const startTime = Date.now();
    setStats(prev => ({ ...prev, startTime }));

    const batches: BulkImage[][] = [];
    for (let i = 0; i < pendingImages.length; i += BATCH_SIZE) {
      batches.push(pendingImages.slice(i, i + BATCH_SIZE));
    }

    let processed = 0;
    let successful = 0;
    let failed = 0;

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      if (abortRef.current) break;

      // Check for pause
      while (pauseRef.current && !abortRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (abortRef.current) break;

      const batch = batches[batchIndex];
      
      setStats(prev => ({
        ...prev,
        currentBatch: batchIndex + 1,
        totalBatches: batches.length
      }));

      // Process batch concurrently
      const results = await Promise.allSettled(
        batch.map(img => enhanceSingleImage(img, settings))
      );

      results.forEach(result => {
        processed++;
        if (result.status === 'fulfilled' && result.value) {
          successful++;
        } else {
          failed++;
        }
      });

      // Calculate time remaining
      const elapsed = Date.now() - startTime;
      const avgTimePerImage = elapsed / processed;
      const remaining = pendingImages.length - processed;
      const estimatedMs = remaining * avgTimePerImage;
      const estimatedMinutes = Math.ceil(estimatedMs / 60000);

      setStats(prev => ({
        ...prev,
        processed,
        successful,
        failed,
        remaining,
        estimatedTimeRemaining: remaining > 0 ? `~${estimatedMinutes} min` : 'Done'
      }));

      // Delay between batches
      if (batchIndex < batches.length - 1 && !abortRef.current) {
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
      }
    }

    setIsProcessing(false);
    
    if (!abortRef.current) {
      toast.success(`Processing complete! ${successful} enhanced, ${failed} failed`);
    }
  };

  const pauseProcessing = () => {
    pauseRef.current = true;
    setIsPaused(true);
    toast.info('Processing paused');
  };

  const resumeProcessing = () => {
    pauseRef.current = false;
    setIsPaused(false);
    toast.info('Processing resumed');
  };

  const stopProcessing = () => {
    abortRef.current = true;
    pauseRef.current = false;
    setIsPaused(false);
    setIsProcessing(false);
    toast.info('Processing stopped');
  };

  const retryFailed = async (settings: EnhancementSettings) => {
    const failedImages = images.filter(img => img.status === 'failed');
    
    // Reset failed images to pending
    setImages(prev => prev.map(img => 
      img.status === 'failed' ? { ...img, status: 'pending', progress: 0, error: undefined } : img
    ));
    
    setStats(prev => ({
      ...prev,
      remaining: prev.remaining + failedImages.length,
      failed: 0
    }));

    toast.info(`Retrying ${failedImages.length} failed images`);
    await processImages(settings);
  };

  const downloadAllAsZip = async () => {
    const completedImages = images.filter(img => img.status === 'completed' && img.enhancedUrl);
    
    if (completedImages.length === 0) {
      toast.error('No enhanced images to download');
      return;
    }

    setIsCreatingZip(true);
    setZipProgress(0);

    try {
      const zip = new JSZip();
      const folder = zip.folder('enhanced-images');

      if (!folder) {
        throw new Error('Failed to create zip folder');
      }

      for (let i = 0; i < completedImages.length; i++) {
        const img = completedImages[i];
        const enhancedUrl = img.enhancedUrl!;
        
        // Extract base64 data
        let base64Data: string;
        let mimeType: string;
        
        if (enhancedUrl.startsWith('data:')) {
          const parts = enhancedUrl.split(',');
          base64Data = parts[1];
          mimeType = parts[0].split(':')[1].split(';')[0];
        } else {
          // If it's a URL, fetch it
          const response = await fetch(enhancedUrl);
          const blob = await response.blob();
          base64Data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              resolve(result.split(',')[1]);
            };
            reader.readAsDataURL(blob);
          });
          mimeType = blob.type;
        }

        const ext = getExtensionFromMime(mimeType);
        const fileName = img.file.name.replace(/\.[^/.]+$/, '');
        
        folder.file(`enhanced-${fileName}.${ext}`, base64Data, { base64: true });
        
        setZipProgress(((i + 1) / completedImages.length) * 100);
      }

      const blob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `enhanced-images-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${completedImages.length} enhanced images as ZIP`);
    } catch (error: any) {
      console.error('ZIP creation error:', error);
      toast.error('Failed to create ZIP file');
    } finally {
      setIsCreatingZip(false);
      setZipProgress(0);
    }
  };

  return {
    images,
    stats,
    isProcessing,
    isPaused,
    isCreatingZip,
    zipProgress,
    addImages,
    removeImage,
    clearAll,
    processImages,
    pauseProcessing,
    resumeProcessing,
    stopProcessing,
    retryFailed,
    downloadAllAsZip
  };
};
